/* eslint-disable */
import { useMemo } from "react";

export function useDerivedData({ items, allItemsStats, totalCount, fields, catalogViewSize, isCurrentlyFiltered, role }) {
  const isAdmin = role === "admin";
  const brandSourceItems = allItemsStats.length > 0 ? allItemsStats : items;
  // Case-insensitive dedupe, preserving first canonical case seen.
  // Priority: fields.brand.options (admin-curated) over items.data.brand (data-side).
  // Mirrors normalizeBrand from useBrandLogic.js — lowercase + trim. Accents not handled
  // here to keep this hook free of cross-imports; brand-merge UI handles accents. T-1024.
  const allBrands = useMemo(() => {
    const canonical = new Map();
    for (const raw of [
      ...(fields.find(f => f.key === "brand")?.options || []),
      ...brandSourceItems.map(i => i.brand).filter(Boolean)
    ]) {
      const trimmed = (raw || "").trim();
      if (!trimmed) continue;
      if (trimmed === "Other" && !isAdmin) continue;
      const key = trimmed.toLowerCase();
      if (!canonical.has(key)) canonical.set(key, trimmed);
    }
    return ["All", ...[...canonical.values()].sort((a, b) => a.localeCompare(b))];
  }, [brandSourceItems, fields, isAdmin]);

  const genderOpts = ["All", ...(fields.find(f => f.key === "gender")?.options || ["Men", "Women", "Unisex"])];
  const typeOpts = ["All", ...(fields.find(f => f.key === "frameCategory")?.options || ["Optic", "Sunglasses"])];
  const matOpts = ["All", ...(fields.find(f => f.key === "material")?.options || ["Acetate", "Metal", "Injection"])];
  const statusOpts = ["All", ...(fields.find(f => f.key === "status")?.options || ["In Development", "Developed", "Archived", "Ordered"])];
  const shapeOpts = ["All", ...(fields.find(f => f.key === "shape1")?.options || [])];
  const assignedOpts = ["All", "Warehouse", ...(fields.find(f => f.key === "assignedTo")?.options || []).filter(o => o.toLowerCase() !== "warehouse")];
  const createdBySource = allItemsStats.length > 0 ? allItemsStats : items;
  const createdByOpts = useMemo(() => [...new Set(createdBySource.map(i => i.created_by_name).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [createdBySource]);

  const pagedItems = useMemo(() => isAdmin ? items : items.filter(i => i.brand !== "Other"), [items, isAdmin]);
  const catalogCols = useMemo(() => {
    // null/undefined = not yet loaded from system_config → default to 0 (6 cols, smallest)
    const v = (catalogViewSize !== null && catalogViewSize !== undefined) ? Number(catalogViewSize) : 0;
    if (v <= 15) return 6;
    if (v <= 30) return 5;
    if (v <= 50) return 4;
    if (v <= 70) return 3;
    if (v <= 85) return 2;
    return 1;
  }, [catalogViewSize]);
  const hasMore = !isCurrentlyFiltered && items.length < totalCount;

  const stats = useMemo(() => {
    const rawSrc = allItemsStats.length > 0 ? allItemsStats : items;
    const src = isAdmin ? rawSrc : rawSrc.filter(i => i.brand !== "Other");
    return {
      total: allItemsStats.length > 0 ? allItemsStats.length : totalCount,
      inStock: src.filter(i => i.status === "In Stock").length,
      display: src.filter(i => i.status === "On Display").length,
      loaned: src.filter(i => i.status === "Loaned Out").length,
      brands: new Set(src.map(i => i.brand)).size,
      inWarehouse: src.filter(i => !i.assignedTo || i.assignedTo.trim() === "" || i.assignedTo.trim().toLowerCase() === "warehouse").length,
      withDesigners: src.filter(i => i.assignedTo && i.assignedTo.trim() && i.assignedTo.trim().toLowerCase() !== "warehouse").length,
      designersList: (() => {
        const map = {};
        src.filter(i => i.assignedTo && i.assignedTo.trim() && i.assignedTo.trim().toLowerCase() !== "warehouse").forEach(i => {
          const key = i.assignedTo.trim(); const n = key.charAt(0).toUpperCase() + key.slice(1);
          if (!map[n]) map[n] = []; map[n].push(i.model || i.sku || "—");
        });
        return map;
      })(),
    };
  }, [allItemsStats, items, totalCount, isAdmin]);

  return { allBrands, genderOpts, typeOpts, matOpts, statusOpts, shapeOpts, assignedOpts, createdByOpts, pagedItems, catalogCols, hasMore, stats };
}
